// src/components/Timeline.jsx
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Calendar,
  Filter,
  ChevronDown,
  ChevronUp,
  Download,
  RefreshCw,
  Search,
  X,
  CheckCircle,
  MessageSquare,
  Phone,
  Mail,
  FileText,
  Home,
  User
} from 'lucide-react';
import TimelineItem from './TimelineItem';
import { useAuth } from '../contexts/useAuth';
import { useNotifications } from '../contexts/NotificationsContext';
import { useIsMobile } from '../hooks/useMediaQuery'; // 👈 NUEVO IMPORT

/**
 * 🎯 Componente Timeline - Linha do tempo unificada e profissional
 * 📱 ADAPTADA PARA DISPOSITIVOS MÓVILES
 */

// Tipos de evento disponibles
const EVENT_TYPES = [
  { id: 'all', label: 'Todos', icon: null, mobileLabel: 'Todos' },
  { id: 'note', label: 'Notas', icon: MessageSquare, color: 'blue', mobileLabel: 'Notas' },
  { id: 'message', label: 'Mensagens', icon: MessageSquare, color: 'green', mobileLabel: 'Msg' },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, color: 'emerald', mobileLabel: 'WA' },
  { id: 'call', label: 'Ligações', icon: Phone, color: 'purple', mobileLabel: 'Lig' },
  { id: 'email', label: 'E-mails', icon: Mail, color: 'amber', mobileLabel: 'Email' },
  { id: 'task', label: 'Tarefas', icon: CheckCircle, color: 'orange', mobileLabel: 'Tarefa' },
  { id: 'meeting', label: 'Reuniões', icon: Calendar, color: 'indigo', mobileLabel: 'Reun' },
  { id: 'document', label: 'Documentos', icon: FileText, color: 'rose', mobileLabel: 'Doc' },
  { id: 'property', label: 'Imóveis', icon: Home, color: 'cyan', mobileLabel: 'Imóv' },
  { id: 'client', label: 'Cliente', icon: User, color: 'slate', mobileLabel: 'Cli' }
];

// Períodos de filtro rápido - adaptados
const QUICK_PERIODS = [
  { id: 'today', label: 'Hoje', mobileLabel: 'Hoje', days: 0 },
  { id: 'yesterday', label: 'Ontem', mobileLabel: 'Ontem', days: 1 },
  { id: 'week', label: 'Esta semana', mobileLabel: '7d', days: 7 },
  { id: 'month', label: 'Este mês', mobileLabel: '30d', days: 30 },
  { id: 'all', label: 'Todo período', mobileLabel: 'Tudo', days: null }
];

// ==========================================================
// FUNÇÕES AUXILIARES PURAS (OTIMIZADAS)
// ==========================================================

const groupEventsByDate = (events) => {
  const groups = {};
  
  events.forEach(event => {
    const date = new Date(event.createdAt || event.timestamp || Date.now());
    const dateKey = date.toISOString().split('T')[0];
    
    if (!groups[dateKey]) {
      groups[dateKey] = {
        date,
        events: [],
        formattedDate: date.toLocaleDateString('pt-BR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })
      };
    }
    
    groups[dateKey].events.push(event);
  });
  
  return Object.values(groups).sort((a, b) => b.date - a.date);
};

const filterEventsByPeriod = (events, period) => {
  if (!period || period === 'all') return events;
  
  const now = new Date();
  const cutoff = new Date();
  
  switch (period) {
    case 'today':
      cutoff.setHours(0, 0, 0, 0);
      break;
    case 'yesterday':
      cutoff.setDate(cutoff.getDate() - 1);
      cutoff.setHours(0, 0, 0, 0);
      break;
    case 'week':
      cutoff.setDate(cutoff.getDate() - 7);
      break;
    case 'month':
      cutoff.setMonth(cutoff.getMonth() - 1);
      break;
    default:
      return events;
  }
  
  return events.filter(event => {
    const eventDate = new Date(event.createdAt || event.timestamp || 0);
    return eventDate >= cutoff;
  });
};

const filterEventsByType = (events, types) => {
  if (!types?.length || types.includes('all')) return events;
  return events.filter(event => types.includes(event.type));
};

const filterEventsBySearch = (events, searchTerm) => {
  if (!searchTerm?.trim()) return events;
  
  const term = searchTerm.toLowerCase().trim();
  return events.filter(event => {
    return (
      event.title?.toLowerCase().includes(term) ||
      event.description?.toLowerCase().includes(term) ||
      event.message?.toLowerCase().includes(term) ||
      event.user?.name?.toLowerCase().includes(term) ||
      event.tags?.some(tag => String(tag).toLowerCase().includes(term))
    );
  });
};

export const Timeline = ({
  events: propEvents = [],
  clientId,
  propertyId,
  onEventClick,
  onEventEdit,
  onEventDelete,
  onEventRelatedClick,
  onRefresh,
  loading = false,
  showFilters = true,
  showSearch = true,
  showQuickPeriods = true,
  showStats = true,
  autoRefresh = false,
  refreshInterval = 30000,
  emptyMessage = 'Nenhum evento encontrado',
  maxHeight = '600px',
  className = ''
}) => {
  const { database } = useAuth();
  const isMobile = useIsMobile(); // 👈 DETECTAR MÓVIL
  
  const notificationsCtx = (() => {
    try {
      return useNotifications();
    } catch {
      return null;
    }
  })();

  const notify = notificationsCtx?.notifySystem ||
    notificationsCtx?.notify ||
    ((msg) => console.log('📅 Timeline:', msg));

  // ==========================================================
  // STATES
  // ==========================================================
  const [filteredEvents, setFilteredEvents] = useState(propEvents);
  const [displayEvents, setDisplayEvents] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState(['all']);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({ total: 0, byType: {}, byDate: 0 });
  
  const timelineRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const isMounted = useRef(true);
  const loadEventsRef = useRef(null);

  // ==========================================================
  // CLEANUP
  // ==========================================================
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, []);

  // ==========================================================
  // CARREGAR EVENTOS
  // ==========================================================
  const loadEvents = useCallback(async () => {
    if (!database) return [];

    try {
      const loadedEvents = [];

      if (propEvents.length > 0) {
        return propEvents;
      }

      if (clientId) {
        const client = await database.getClientById(clientId);
        
        if (client?.notes?.length) {
          loadedEvents.push(...client.notes.map(note => ({
            ...note,
            type: 'note',
            typeLabel: 'Nota',
            user: note.createdBy ? { id: note.createdBy } : null
          })));
        }

        if (database.getMessagesByClient) {
          const messages = await database.getMessagesByClient(clientId);
          if (messages?.length) {
            loadedEvents.push(...messages.map(msg => ({
              ...msg,
              type: 'whatsapp',
              typeLabel: 'WhatsApp',
              description: msg.message
            })));
          }
        }

        if (database.getTasksByClient) {
          const tasks = await database.getTasksByClient(clientId);
          if (tasks?.length) {
            loadedEvents.push(...tasks.map(task => ({
              ...task,
              type: task.completed ? 'task_completed' : 'task',
              typeLabel: 'Tarefa',
              description: task.description
            })));
          }
        }

        if (database.getDocumentsByClient) {
          const docs = await database.getDocumentsByClient(clientId);
          if (docs?.length) {
            loadedEvents.push(...docs.map(doc => ({
              ...doc,
              type: 'document',
              typeLabel: 'Documento',
              title: doc.title || doc.fileName
            })));
          }
        }
      }

      if (propertyId && database.getDocumentsByProperty) {
        const docs = await database.getDocumentsByProperty(propertyId);
        if (docs?.length) {
          loadedEvents.push(...docs.map(doc => ({
            ...doc,
            type: 'document',
            typeLabel: 'Documento',
            title: doc.title || doc.fileName
          })));
        }
      }

      return loadedEvents.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.timestamp || 0);
        const dateB = new Date(b.createdAt || b.timestamp || 0);
        return dateB - dateA;
      });

    } catch (error) {
      console.error('❌ Erro ao carregar eventos:', error);
      notify({
        type: 'danger',
        title: 'Erro ao carregar timeline',
        message: 'Não foi possível carregar o histórico.'
      });
      return [];
    }
  }, [database, clientId, propertyId, propEvents, notify]);

  useEffect(() => {
    loadEventsRef.current = loadEvents;
  }, [loadEvents]);

  // ==========================================================
  // EFEITO INICIAL
  // ==========================================================
  useEffect(() => {
    let isActive = true;

    const fetchEvents = async () => {
      if (!loadEventsRef.current) return;
      
      const events = await loadEventsRef.current();
      if (isActive && isMounted.current) {
        setFilteredEvents(prev => {
          if (JSON.stringify(prev) === JSON.stringify(events)) return prev;
          return events;
        });
      }
    };

    if (propEvents.length > 0) {
      setFilteredEvents(propEvents);
    } else if (clientId || propertyId) {
      fetchEvents();
    }

    return () => {
      isActive = false;
    };
  }, [clientId, propertyId, propEvents]);

  // ==========================================================
  // AUTO-REFRESH
  // ==========================================================
  useEffect(() => {
    if (!autoRefresh || (!clientId && !propertyId)) return;

    refreshTimerRef.current = setInterval(async () => {
      if (!isMounted.current || !loadEventsRef.current) return;
      
      setRefreshing(true);
      const newEvents = await loadEventsRef.current();
      if (isMounted.current) {
        setFilteredEvents(prev => {
          if (JSON.stringify(prev) === JSON.stringify(newEvents)) return prev;
          return newEvents;
        });
        setRefreshing(false);
      }
    }, refreshInterval);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, clientId, propertyId]);

  // ==========================================================
  // APLICAR FILTROS
  // ==========================================================
  useEffect(() => {
    const filtered = filterEventsBySearch(
      filterEventsByType(
        filterEventsByPeriod(filteredEvents, selectedPeriod),
        selectedTypes
      ),
      searchTerm
    );

    setDisplayEvents(filtered);

    if (filtered.length > 0) {
      const byType = filtered.reduce((acc, event) => {
        acc[event.type] = (acc[event.type] || 0) + 1;
        return acc;
      }, {});
      
      setStats({
        total: filtered.length,
        byType,
        byDate: groupEventsByDate(filtered).length
      });
    } else {
      setStats({ total: 0, byType: {}, byDate: 0 });
    }
  }, [filteredEvents, selectedPeriod, selectedTypes, searchTerm]);

  // ==========================================================
  // HANDLERS
  // ==========================================================
  const handleRefresh = useCallback(async () => {
    if (!loadEventsRef.current) return;
    
    setRefreshing(true);
    const newEvents = await loadEventsRef.current();
    
    if (isMounted.current) {
      setFilteredEvents(newEvents);
      setRefreshing(false);
      
      if (onRefresh) onRefresh(newEvents);
      
      notify({
        type: 'success',
        title: 'Timeline atualizada',
        message: 'Os eventos foram atualizados.'
      });
    }
  }, [notify, onRefresh]);

  const handleTypeToggle = useCallback((typeId) => {
    setSelectedTypes(prev => {
      if (typeId === 'all') return ['all'];
      
      const withoutAll = prev.filter(t => t !== 'all');
      const newTypes = withoutAll.includes(typeId)
        ? withoutAll.filter(t => t !== typeId)
        : [...withoutAll, typeId];
      
      return newTypes.length === 0 ? ['all'] : newTypes;
    });
  }, []);

  const handlePeriodSelect = useCallback((periodId) => {
    setSelectedPeriod(periodId);
  }, []);

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // ==========================================================
  // MEMO: Eventos agrupados
  // ==========================================================
  const groupedEvents = useMemo(() => 
    groupEventsByDate(displayEvents),
    [displayEvents]
  );

  // ==========================================================
  // RENDER - ADAPTADO
  // ==========================================================
  return (
    <div
      ref={timelineRef}
      className={`bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden ${className}`}
    >
      
      {/* Header - ADAPTADO */}
      <div className={`border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white ${
        isMobile ? 'px-4 py-3' : 'px-6 py-4'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`${isMobile ? 'p-1.5' : 'p-2'} bg-purple-100 rounded-xl`}>
              <Calendar className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} style={{ color: '#8b5cf6' }} />
            </div>
            <div>
              <h2 className={`font-bold text-slate-900 ${isMobile ? 'text-sm' : 'text-lg'}`}>
                {isMobile ? 'Timeline' : 'Linha do Tempo'}
              </h2>
              <p className={`text-slate-500 ${isMobile ? 'text-[9px]' : 'text-xs'}`}>
                {stats.total} {stats.total === 1 ? 'evento' : 'eventos'}
              </p>
            </div>
          </div>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`hover:bg-slate-100 rounded-lg transition-colors ${
              isMobile ? 'p-1.5' : 'p-2'
            }`}
            title="Atualizar"
          >
            <RefreshCw className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-slate-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {(showFilters || showSearch || showQuickPeriods) && (
          <div className={`${isMobile ? 'mt-3 space-y-2' : 'mt-4 space-y-3'}`}>
            
            {/* Busca - ADAPTADA */}
            {showSearch && (
              <div className="relative">
                <Search className={`absolute left-2 top-1/2 transform -translate-y-1/2 ${
                  isMobile ? 'w-3 h-3' : 'w-4 h-4'
                } text-slate-400`} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  placeholder={isMobile ? "Buscar..." : "Buscar na timeline..."}
                  className={`w-full border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-200 ${
                    isMobile 
                      ? 'pl-7 pr-6 py-1.5 text-xs' 
                      : 'pl-9 pr-8 py-2 text-sm'
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className={`absolute right-2 top-1/2 transform -translate-y-1/2`}
                  >
                    <X className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-slate-400 hover:text-slate-600`} />
                  </button>
                )}
              </div>
            )}

            <div className={`flex flex-wrap gap-1`}>
              
              {/* Filtro de tipos - ADAPTADO */}
              {showFilters && (
                <div className="relative">
                  <button
                    onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                    className={`flex items-center gap-1 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors touch:active:scale-95 ${
                      isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                    }`}
                  >
                    <Filter className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                    <span>
                      {isMobile
                        ? (selectedTypes.includes('all') ? 'Tipos' : `${selectedTypes.length}`)
                        : (selectedTypes.includes('all') ? 'Todos os tipos' : `${selectedTypes.length} tipo${selectedTypes.length > 1 ? 's' : ''}`)
                      }
                    </span>
                    {showTypeDropdown ? (
                      <ChevronUp className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                    ) : (
                      <ChevronDown className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                    )}
                  </button>

                  {showTypeDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowTypeDropdown(false)}
                      />
                      <div className={`absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-slate-200 z-50 p-1 ${
                        isMobile ? 'w-40' : 'w-64'
                      }`}>
                        <div className="space-y-0.5">
                          <button
                            onClick={() => handleTypeToggle('all')}
                            className={`w-full text-left rounded-lg transition-colors ${
                              isMobile ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'
                            } ${
                              selectedTypes.includes('all')
                                ? 'bg-purple-100 text-purple-700'
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            {isMobile ? 'Todos' : 'Todos os tipos'}
                          </button>
                          <div className="border-t border-slate-100 my-0.5" />
                          {EVENT_TYPES.filter(t => t.id !== 'all').map(type => (
                            <button
                              key={type.id}
                              onClick={() => handleTypeToggle(type.id)}
                              className={`w-full text-left rounded-lg transition-colors flex items-center gap-1 ${
                                isMobile ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'
                              } ${
                                selectedTypes.includes(type.id)
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'hover:bg-slate-50'
                              }`}
                            >
                              {type.icon && <type.icon className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />}
                              <span>{isMobile ? type.mobileLabel : type.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Períodos - ADAPTADOS */}
              {showQuickPeriods && (
                <div className={`flex gap-0.5`}>
                  {QUICK_PERIODS.map(period => (
                    <button
                      key={period.id}
                      onClick={() => handlePeriodSelect(period.id)}
                      className={`rounded-lg transition-colors touch:active:scale-95 ${
                        isMobile 
                          ? 'px-2 py-1 text-[9px]' 
                          : 'px-3 py-1.5 text-sm'
                      } ${
                        selectedPeriod === period.id
                          ? 'bg-purple-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {isMobile ? period.mobileLabel : period.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Timeline Content - ADAPTADO */}
      <div
        className={`overflow-y-auto ${isMobile ? 'p-3' : 'p-6'}`}
        style={{ maxHeight: isMobile ? '350px' : maxHeight }}
      >
        {loading ? (
          <div className={`space-y-4`}>
            {[1, 2].map(i => (
              <div key={i} className="animate-pulse">
                <div className={`${isMobile ? 'h-4 w-32 mb-2' : 'h-6 w-48 mb-4'} bg-slate-200 rounded`} />
                <div className="space-y-2">
                  <div className={`${isMobile ? 'h-14' : 'h-20'} bg-slate-100 rounded-xl`} />
                  <div className={`${isMobile ? 'h-14' : 'h-20'} bg-slate-100 rounded-xl`} />
                </div>
              </div>
            ))}
          </div>
        ) : groupedEvents.length === 0 ? (
          <div className={`text-center ${isMobile ? 'py-8 px-3' : 'py-12 px-4'}`}>
            <div className={`mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-3 ${
              isMobile ? 'w-12 h-12' : 'w-16 h-16'
            }`}>
              <Calendar className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} text-slate-400`} />
            </div>
            <h3 className={`font-medium text-slate-700 mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              Nenhum evento encontrado
            </h3>
            <p className={`text-slate-400 max-w-sm mx-auto ${isMobile ? 'text-[9px]' : 'text-xs'}`}>
              {searchTerm || !selectedTypes.includes('all') || selectedPeriod !== 'all'
                ? 'Ajuste os filtros.'
                : emptyMessage}
            </p>
          </div>
        ) : (
          <div className={`space-y-4`}>
            {groupedEvents.map((group) => (
              <TimelineItem.Group key={group.date.toISOString()} date={group.formattedDate} isMobile={isMobile}>
                {group.events.map((event, index) => (
                  <TimelineItem
                    key={`${event.id}-${index}`}
                    event={event}
                    onEdit={onEventEdit}
                    onDelete={onEventDelete}
                    onClick={onEventClick}
                    onRelatedClick={onEventRelatedClick}
                    isLast={index === group.events.length - 1}
                    isMobile={isMobile}
                  />
                ))}
              </TimelineItem.Group>
            ))}
          </div>
        )}
      </div>

      {/* Footer - ADAPTADO */}
      {showStats && stats.total > 0 && (
        <div className={`border-t border-slate-200 bg-slate-50 ${
          isMobile ? 'px-3 py-2' : 'px-6 py-3'
        }`}>
          <div className={`flex items-center justify-between ${isMobile ? 'text-[8px]' : 'text-xs'}`}>
            <div className="flex items-center gap-2">
              <span className="text-slate-600">
                Total: <strong className="text-slate-900">{stats.total}</strong>
              </span>
              {!isMobile && Object.entries(stats.byType).slice(0, 3).map(([type, count]) => {
                const typeInfo = EVENT_TYPES.find(t => t.id === type);
                return (
                  <span key={type} className="text-slate-600 flex items-center gap-1">
                    {typeInfo?.icon && <typeInfo.icon className="w-3 h-3" />}
                    {count}
                  </span>
                );
              })}
            </div>
            <button
              onClick={() => console.log('Exportar timeline')}
              className="text-purple-600 hover:text-purple-700 flex items-center gap-1"
            >
              <Download className={`${isMobile ? 'w-3 h-3' : 'w-3 h-3'}`} />
              {!isMobile && 'Exportar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Componentes específicos (sin cambios)
Timeline.ForClient = ({ clientId, ...props }) => (
  <Timeline clientId={clientId} {...props} />
);

Timeline.ForProperty = ({ propertyId, ...props }) => (
  <Timeline propertyId={propertyId} {...props} />
);

Timeline.Demo = () => {
  const mockEvents = [
    {
      id: '1',
      type: 'note',
      title: 'Nota importante',
      description: 'Cliente prefere imóveis com 3 quartos e área de lazer',
      createdAt: new Date().toISOString(),
      user: { name: 'João Silva' },
      tags: ['importante', 'preferencia']
    },
    {
      id: '2',
      type: 'whatsapp',
      title: 'Conversa WhatsApp',
      description: 'Enviei fotos do apartamento na Vila Mariana',
      createdAt: new Date(Date.now() - 3600000).toISOString(),
      user: { name: 'Maria Santos' },
      attachments: [{ name: 'foto1.jpg' }, { name: 'foto2.jpg' }]
    },
    {
      id: '3',
      type: 'task',
      title: 'Ligar para cliente',
      description: 'Confirmar interesse no imóvel',
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      user: { name: 'João Silva' },
      status: 'pending'
    },
    {
      id: '4',
      type: 'document',
      title: 'Contrato de proposta',
      description: 'Documento assinado pelo cliente',
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      user: { name: 'Maria Santos' },
      attachments: [{ name: 'contrato.pdf' }]
    }
  ];

  return <Timeline events={mockEvents} showFilters showSearch />;
};

export default Timeline;